<template>
  <div class="space-y-6">
    <!-- DEBUG: Test Modal Always Visible -->
    <div style="position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 999; background: white; padding: 20px; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); display: none;" id="test-modal">
      <h1>TEST MODAL - If you see this, Vue rendering works</h1>
      <p>showForm value: {{ showForm }}</p>
      <button @click="testClick">Test Click</button>
    </div>

    <div class="bg-white rounded-lg shadow p-6">
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-xl font-bold">Districts</h2>
        <button 
          @click="handleClick"
          type="button"
          class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          ➕ New District
        </button>
      </div>

      <div v-if="masterDataStore.loading" class="text-center py-12">
        <div class="inline-block spinner w-8 h-8"></div>
      </div>
      <div v-else-if="masterDataStore.districts.length === 0" class="text-center py-12 text-gray-500">
        No districts found
      </div>
      <div v-else class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-100 border-b">
            <tr>
              <th class="px-6 py-3 text-left text-sm font-semibold">ID</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Code</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Name</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Description</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Status</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y">
            <tr v-for="district in masterDataStore.districts" :key="district.id" class="hover:bg-gray-50">
              <td class="px-6 py-4 text-sm text-gray-500">{{ district.id }}</td>
              <td class="px-6 py-4 text-sm font-medium">{{ district.code }}</td>
              <td class="px-6 py-4 text-sm">{{ district.name }}</td>
              <td class="px-6 py-4 text-sm">{{ district.description }}</td>
              <td class="px-6 py-4 text-sm">
                <span :class="[
                  'px-2 py-1 rounded text-xs',
                  String(district.is_active) === '1' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                ]">
                  {{ String(district.is_active) === '1' ? 'Active' : 'Inactive' }}
                </span>
              </td>
              <td class="px-6 py-4 text-sm space-x-2">
                <button @click="editDistrict(district)" class="text-blue-600 hover:text-blue-800">Edit</button>
                <button @click="deleteDistrictFunc(district.id)" class="text-red-600 hover:text-red-800">Delete</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Form Modal -->
    <div v-if="showForm" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 50;">
      <div style="background: white; padding: 24px; border-radius: 8px; width: 384px; position: relative; z-index: 51;">
        <h3 style="font-size: 18px; font-weight: bold; margin-bottom: 16px;">{{ editingDistrict ? 'Edit District' : 'New District' }}</h3>
        <form @submit.prevent="submitForm" style="display: flex; flex-direction: column; gap: 16px;">
          <div>
            <label style="display: block; font-size: 14px; font-weight: 500; margin-bottom: 4px;">Code</label>
            <input v-model="formData.code" placeholder="e.g., D001" required style="width: 100%; padding: 8px; border: 1px solid #ccc; border-radius: 4px;" />
          </div>
          <div>
            <label style="display: block; font-size: 14px; font-weight: 500; margin-bottom: 4px;">Name</label>
            <input v-model="formData.name" placeholder="e.g., Mumbai" required style="width: 100%; padding: 8px; border: 1px solid #ccc; border-radius: 4px;" />
          </div>
          <div>
            <label style="display: block; font-size: 14px; font-weight: 500; margin-bottom: 4px;">Description</label>
            <textarea v-model="formData.description" placeholder="Enter description" rows="3" style="width: 100%; padding: 8px; border: 1px solid #ccc; border-radius: 4px;"></textarea>
          </div>
          <div style="display: flex; align-items: center;">
            <input v-model="formData.is_active" type="checkbox" id="active-check" />
            <label for="active-check" style="margin-left: 8px; font-size: 14px;">Active</label>
          </div>
          <div style="display: flex; gap: 12px; padding-top: 16px; border-top: 1px solid #ccc;">
            <button type="button" @click="closeForm" style="flex: 1; padding: 8px; border: 1px solid #ccc; border-radius: 4px; cursor: pointer;">Cancel</button>
            <button type="submit" style="flex: 1; padding: 8px; background: #2563eb; color: white; border: none; border-radius: 4px; cursor: pointer;">{{ editingDistrict ? 'Update' : 'Create' }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Swal from 'sweetalert2'
import { useMasterDataStore } from '@/stores/masterDataStore'

const masterDataStore = useMasterDataStore()
const showForm = ref(false)
const editingDistrict = ref(null)
const formData = ref({ code: '', name: '', description: '', is_active: true })

const submitForm = async () => {
  try {
    if (!formData.value.code || !formData.value.name) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Fields',
        text: 'Code and name are required',
        confirmButtonColor: '#3085d6'
      })
      return
    }
    const data = {
      code: formData.value.code,
      name: formData.value.name,
      description: formData.value.description,
      is_active: formData.value.is_active ? 1 : 0
    }
    if (editingDistrict.value) {
      await masterDataStore.updateDistrict(editingDistrict.value.id, data)
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'District updated successfully',
        confirmButtonColor: '#3085d6'
      })
    } else {
      console.log('Creating district with data:', data)
      const result = await masterDataStore.createDistrict(data)
      console.log('Create result:', result)
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'District created successfully',
        confirmButtonColor: '#3085d6'
      })
    }
    closeForm()
    console.log('Refreshing districts list...')
    const districts = await masterDataStore.getDistricts()
    console.log('Districts after refresh:', districts)
    console.log('Store districts:', masterDataStore.districts)
  } catch (error) {
    console.error('Form submission error:', error)
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: error.message || 'Failed to save',
      confirmButtonColor: '#3085d6'
    })
  }
}

const closeForm = () => {
  showForm.value = false
  editingDistrict.value = null
  formData.value = { code: '', name: '', description: '', is_active: true }
}

const openForm = () => {
  console.log('openForm called')
  showForm.value = true
}

const handleClick = () => {
  console.log('Button clicked! Current showForm value:', showForm.value)
  showForm.value = true
  console.log('After setting, showForm value:', showForm.value)
}

const testClick = () => {
  console.log('Test click works!')
  Swal.fire({
    icon: 'info',
    title: 'Info',
    text: 'Vue is responding!',
    confirmButtonColor: '#3085d6'
  })
}

const editDistrict = (district) => {
  editingDistrict.value = district
  formData.value = { ...district }
  showForm.value = true
}

const deleteDistrictFunc = async (id) => {
  Swal.fire({
    title: 'Confirm Delete',
    text: 'Are you sure you want to delete this district?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Yes, delete it',
    cancelButtonText: 'Cancel'
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        await masterDataStore.deleteDistrict(id)
        await masterDataStore.getDistricts()
        Swal.fire({
          icon: 'success',
          title: 'Deleted',
          text: 'District deleted successfully',
          confirmButtonColor: '#3085d6'
        })
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Delete Failed',
          text: error.response?.data?.message || masterDataStore.error || 'Failed to delete district',
          confirmButtonColor: '#3085d6'
        })
      }
    }
  })
}

onMounted(async () => {
  console.log('DistrictsPage mounted - loading districts...')
  await masterDataStore.getDistricts()
  console.log('Districts loaded:', masterDataStore.districts)
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
